import React from 'react';
import { Search, Loader2, X, ShieldCheck, Check } from 'lucide-react';
import { LeagueDB } from '../../services/footballService';
import { t } from '../../i18n';
import { Language } from '../../types';
import { getLeagueName, LEAGUE_TRANSLATIONS } from '../../services/leagueService';

interface LeagueSelectionContentProps {
    isInitial: boolean;
    lang: Language;
    leagueSearchQuery: string;
    setLeagueSearchQuery: (query: string) => void;
    isSearching: boolean;
    scoutedResults: LeagueDB[];
    leaguePool: LeagueDB[];
    selectedLeagueIds: string[];
    handleLeagueToggle: (league: LeagueDB) => void;
    handleSelectionDone: () => void;
    handleCloseSelection: () => void;
    isSaving: boolean;
    MAJOR_LEAGUE_IDS: string[];
    MAJOR_CUP_IDS: string[];
}

export const LeagueSelectionContent: React.FC<LeagueSelectionContentProps> = React.memo(({
    isInitial, lang, leagueSearchQuery, setLeagueSearchQuery, isSearching,
    scoutedResults, leaguePool, selectedLeagueIds, handleLeagueToggle,
    handleSelectionDone, handleCloseSelection, isSaving, MAJOR_LEAGUE_IDS, MAJOR_CUP_IDS
}) => {
    const isSearchingMode = leagueSearchQuery.length >= 1;
    const majorLeagues = leaguePool.filter(l => MAJOR_LEAGUE_IDS.includes(l.id));
    const majorCups = leaguePool.filter(l => MAJOR_CUP_IDS.includes(l.id));
    const extraList = leaguePool.filter(l => !MAJOR_LEAGUE_IDS.includes(l.id) && !MAJOR_CUP_IDS.includes(l.id));

    const renderLeagueGrid = (list: LeagueDB[]) => (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-10 justify-items-center">
            {list.map((league) => {
                const isSelected = selectedLeagueIds.includes(league.id);
                return (
                    <div key={league.id} onClick={() => handleLeagueToggle(league)} className="flex flex-col items-center gap-3 cursor-pointer group w-full transition-all">
                        <div className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 bg-white border-2 ${isSelected ? 'border-rose-500 scale-110 shadow-xl shadow-rose-100' : 'border-slate-50 md:group-hover:border-slate-200'}`}>
                            <div className="w-10 h-10 sm:w-12 sm:h-12">
                                <img src={league.id === '1' ? "/assets/fiwc.png" : league.logo_url} alt={league.name} className="w-full h-full object-contain" loading="lazy" />
                            </div>
                            {isSelected && <div className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-md animate-in zoom-in duration-300"><Check size={12} strokeWidth={4} /></div>}
                        </div>
                        <span className={`text-[9px] font-black text-center uppercase tracking-tighter transition-colors w-full line-clamp-2 leading-tight px-1 ${isSelected ? 'text-rose-600' : 'text-slate-400'}`}>
                            {getLeagueName(parseInt(league.id), lang, league.name)}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className={`flex flex-col h-full bg-white font-sans ${isInitial ? '' : 'rounded-t-[40px] md:rounded-[40px]'}`}>
            <div className={`sticky top-0 z-40 bg-white/95 backdrop-blur-md px-6 flex items-center justify-between ${isInitial ? 'pt-12 pb-6 border-b border-slate-50' : 'pt-0 md:pt-8 pb-2'}`}>
                <div className="flex flex-col">
                    <h1 className="text-xl font-extrabold text-slate-800 tracking-tight uppercase">{isInitial ? t(lang, 'manageLeagues') : t(lang, 'updateSectors')}</h1>
                    {!isInitial && <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{t(lang, 'configureIntelSources')}</span>}
                </div>
                <div className="flex items-center gap-4">
                    {!isInitial ? (
                        <button onClick={handleCloseSelection} className="p-2.5 text-slate-400 md:hover:text-slate-900 transition-colors bg-slate-50 rounded-full"><X size={20} strokeWidth={3} /></button>
                    ) : (
                        <button onClick={handleSelectionDone} disabled={selectedLeagueIds.length === 0 || isSaving} className="bg-slate-900 text-white px-6 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest disabled:opacity-30 shadow-lg shadow-slate-200">{isSaving ? <Loader2 className="animate-spin" size={16} /> : t(lang, 'save')}</button>
                    )}
                </div>
            </div>
            <div className="px-6 mb-6 mt-4">
                <div className="relative">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={18} strokeWidth={2.5} />
                    <input 
                        type="text" 
                        placeholder={t(lang, 'searchAllLeagues')} 
                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-12 text-sm font-bold text-slate-800 placeholder:text-slate-300 outline-none transition-all shadow-sm focus:bg-white" 
                        value={leagueSearchQuery} 
                        onChange={(e) => setLeagueSearchQuery(e.target.value)} 
                    />
                    {leagueSearchQuery && <button onClick={() => setLeagueSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 bg-slate-200 text-slate-500 rounded-full flex items-center justify-center md:hover:bg-slate-300 transition-all active:scale-90"><X size={14} strokeWidth={3} /></button>}
                </div>
                {isSearching && <div className="flex items-center gap-2 mt-3 px-2 animate-in fade-in slide-in-from-top-1"><Loader2 size={12} className="text-rose-500 animate-spin" /><span className="text-[10px] font-black text-rose-500 uppercase tracking-widest">{t(lang, 'searchingGlobalDatabase')}</span></div>}
            </div>
            <div className="flex-1 overflow-y-auto pt-2 px-6 pb-32 custom-scrollbar">
                {isSearchingMode ? (
                    <div className="animate-in fade-in duration-300">
                        <div className="mb-4 px-2">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{t(lang, 'matchesFound')}</h3>
                            {scoutedResults.length > 0 ? renderLeagueGrid(
                              scoutedResults.filter(l => {
                                const q = leagueSearchQuery.toLowerCase();
                                const originalName = l.name.toLowerCase();
                                const currentLangName = getLeagueName(Number(l.id), lang, l.name).toLowerCase();
                                if (currentLangName.includes(q) || originalName.includes(q)) return true;

                                const translation = LEAGUE_TRANSLATIONS.find(t => t.league_id === Number(l.id));
                                return translation ? Object.values(translation.names).some(name => name.toLowerCase().includes(q)) : false;
                              })
                            ) : <div className="py-20 text-center opacity-40 text-xs font-bold uppercase tracking-widest">{t(lang, 'noMatchingSectors')}</div>}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {majorLeagues.length > 0 && <section><h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><div className="w-1 h-3 bg-rose-500 rounded-full"></div>{t(lang, 'majorLeagues')}</h3>{renderLeagueGrid(majorLeagues)}</section>}
                        {majorCups.length > 0 && <section><h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><div className="w-1 h-3 bg-indigo-500 rounded-full"></div>{t(lang, 'majorCups')}</h3>{renderLeagueGrid(majorCups)}</section>}
                        {extraList.length > 0 && <section className="pb-10"><h3 className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><div className="w-1 h-3 bg-emerald-500 rounded-full"></div>{t(lang, 'addedSectors')}</h3>{renderLeagueGrid(extraList)}</section>}
                    </div>
                )}
            </div>
            {!isInitial && (
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/80 backdrop-blur-md border-t border-slate-50 z-50 md:rounded-b-[40px]">
                    <button 
                        onClick={handleSelectionDone} 
                        disabled={selectedLeagueIds.length === 0 || isSaving} 
                        className="w-full bg-slate-900 text-white py-4 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-500 transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 size={18} className="animate-spin" /> : <><ShieldCheck size={18} /> {t(lang, 'saveChanges')}</>}
                    </button>
                </div>
            )}
        </div>
    );
});

LeagueSelectionContent.displayName = 'LeagueSelectionContent';
